import { FormEvent, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { getApiErrorMessage, portalApi } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { TOKEN_STORAGE_KEY } from '../../config';
import { useAuth } from '../../context/AuthContext';

export function RegisterPage() {
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailExistsHint, setEmailExistsHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLock = useRef(false);

  const completeRegistration = (token: string) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    setToken(token);
    navigate('/kyb', { replace: true });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitLock.current || isSubmitting) return;

    submitLock.current = true;
    setIsSubmitting(true);
    setError('');
    setEmailExistsHint(false);

    const normalizedEmail = email.trim().toLowerCase();

    try {
      const res = await portalApi.register({
        company_name: companyName,
        email: normalizedEmail,
        password,
      });
      if (!res.ok || !res.token) throw new Error(res.error ?? 'Registration failed');
      completeRegistration(res.token);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        try {
          const loginRes = await portalApi.login(normalizedEmail, password);
          if (loginRes.ok && loginRes.token && !loginRes.requires_2fa) {
            completeRegistration(loginRes.token);
            return;
          }
        } catch {
          /* account exists but password differs */
        }
        let diag = '';
        try {
          const check = await portalApi.checkEmail(normalizedEmail);
          if (check.ok) {
            if (check.email_taken || check.portal_user) {
              diag =
                ' Account exists in merchant_users. Sign in with your portal password, or complete pending onboarding with the same password.';
            } else {
              diag =
                ' API returned conflict but email is not in merchant_users — redeploy BFF and check DATABASE_URL on the server.';
            }
          }
        } catch {
          /* ignore */
        }
        setError(`This email is already registered.${diag}`);
        setEmailExistsHint(true);
      } else {
        setError(getApiErrorMessage(err));
      }
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <form onSubmit={onSubmit} className="glass-panel w-full max-w-md p-8 space-y-5">
        <p className="font-display text-2xl text-center">Create merchant account</p>
        {error ? <p className="text-sm text-onyx-danger">{error}</p> : null}
        {emailExistsHint ? (
          <p className="text-sm text-onyx-muted text-center">
            Your account may already exist from a previous attempt.{' '}
            <Link to="/login" className="text-onyx-gold hover:underline">
              Sign in instead
            </Link>
          </p>
        ) : null}
        <fieldset disabled={isSubmitting} className="space-y-5 border-0 p-0 m-0 min-w-0">
          <label className="block text-sm text-onyx-muted">
            Company name
            <Input className="mt-1" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
          </label>
          <label className="block text-sm text-onyx-muted">
            Owner email
            <Input type="email" className="mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="block text-sm text-onyx-muted">
            Password (min 8)
            <Input
              type="password"
              className="mt-1"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <Button type="submit" className="w-full" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 size={18} className="animate-spin" aria-hidden />
                Registering…
              </>
            ) : (
              'Register'
            )}
          </Button>
        </fieldset>
        <p className="text-center text-sm text-onyx-muted">
          <Link to="/login" className="text-onyx-gold hover:underline">
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
